/**
 * Input.ts — Movement and actions, from the keyboard or a floating virtual stick.
 *
 * Everything funnels through `getDirection()`, so entities and physics never learn
 * where the input came from. The vector it returns has length 0..1: the keyboard
 * is all-or-nothing (length 1), while the stick is **analog** — a small drag
 * ambles, a full push strides.
 *
 * Pointer Events do the touch and mouse work together: a finger and a held mouse
 * button produce the same `pointerdown → pointermove → pointerup` sequence, so
 * "press and drag to walk" is one implementation rather than two. The stick is
 * *floating* — wherever you press becomes its origin.
 *
 * On-screen controls call the `pressVirtual*` setters, so the rest of the game
 * keeps asking the same questions it asked of the keyboard.
 *
 * There is **no action key left**. Space used to be the cleanup hold; that step
 * is gone (see `types.ts` → `Objective`), so the whole game is steering plus a
 * restart tap, and `isActionDown` / `setVirtualAction` went with it. If you add
 * an action back, pair a query with a virtual setter the way
 * `wasRestartPressed` / `pressVirtualRestart` do, so the keyboard and any
 * on-screen control reach the game through one path.
 */
import { TOUCH } from '../config/constants';
import { readJoystick, knobOffset, isInJoystickZone } from '../systems/joystick';
import type { InputScheme } from '../types';
import type { Vec2 } from '../utils/vec2';

/** Live stick state, for drawing it. */
export interface JoystickView {
  active: boolean;
  /** Screen position the stick is anchored at. */
  originX: number;
  originY: number;
  /** Knob position relative to the origin, already clamped to the ring. */
  knobX: number;
  knobY: number;
}

export class Input {
  private down = new Set<string>();
  private pressedThisFrame = new Set<string>();
  private detach: () => void;

  // --- virtual stick ---------------------------------------------------------
  /** id of the pointer driving the stick; undefined when idle. Tracking exactly
   *  one means a second finger can't hijack it. */
  private stickPointer?: number;
  private originX = 0;
  private originY = 0;
  private dragX = 0;
  private dragY = 0;

  /** Set for one frame when an on-screen restart is tapped. */
  private virtualRestart = false;

  /** Called whenever the stick appears, moves, or disappears — drives its visual. */
  onJoystickChange?: (view: JoystickView) => void;

  /**
   * What the player is actually using, so hints can name the right control.
   * Seeded from the device, then corrected by the first real input — a device
   * guess alone is wrong on hybrids and stays wrong all run.
   */
  private _scheme: InputScheme =
    typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
      ? 'pointer'
      : 'keys';

  get scheme(): InputScheme {
    return this._scheme;
  }

  constructor(
    target: Window | HTMLElement = window,
    private pointerTarget: HTMLElement = document.body,
  ) {
    const onDown = (e: Event): void => {
      const key = (e as KeyboardEvent).code;
      if (!this.down.has(key)) this.pressedThisFrame.add(key);
      this.down.add(key);
      // A key that actually does something means they're playing on a keyboard.
      if (MOVEMENT_KEYS.has(key)) this._scheme = 'keys';
      // Stop the arrows from scrolling the page.
      if (MOVEMENT_KEYS.has(key)) e.preventDefault();
    };
    const onUp = (e: Event): void => {
      this.down.delete((e as KeyboardEvent).code);
    };
    // Losing focus mid-key would otherwise leave the player walking forever.
    const onBlur = (): void => {
      this.down.clear();
      this.endStick();
    };

    target.addEventListener('keydown', onDown);
    target.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);

    const onPointerDown = (e: PointerEvent): void => {
      // One stick at a time, and not in the HUD strip along the top.
      if (this.stickPointer !== undefined) return;
      if (!isInJoystickZone(e.clientY, window.innerHeight, TOUCH.ZONE_TOP_FRACTION)) return;

      this._scheme = 'pointer';
      this.stickPointer = e.pointerId;
      this.originX = e.clientX;
      this.originY = e.clientY;
      this.dragX = 0;
      this.dragY = 0;
      capturePointer(this.pointerTarget, e.pointerId);
      this.emitJoystick();
    };
    const onPointerMove = (e: PointerEvent): void => {
      if (e.pointerId !== this.stickPointer) return;
      this.dragX = e.clientX - this.originX;
      this.dragY = e.clientY - this.originY;
      this.emitJoystick();
    };
    const onPointerUp = (e: PointerEvent): void => {
      if (e.pointerId !== this.stickPointer) return;
      releasePointer(this.pointerTarget, e.pointerId);
      this.endStick();
    };

    this.pointerTarget.addEventListener('pointerdown', onPointerDown);
    this.pointerTarget.addEventListener('pointermove', onPointerMove);
    this.pointerTarget.addEventListener('pointerup', onPointerUp);
    // A cancelled pointer (gesture taken over by the OS, a call arriving) would
    // otherwise strand the stick and walk the player off forever.
    this.pointerTarget.addEventListener('pointercancel', onPointerUp);

    this.detach = () => {
      target.removeEventListener('keydown', onDown);
      target.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
      this.pointerTarget.removeEventListener('pointerdown', onPointerDown);
      this.pointerTarget.removeEventListener('pointermove', onPointerMove);
      this.pointerTarget.removeEventListener('pointerup', onPointerUp);
      this.pointerTarget.removeEventListener('pointercancel', onPointerUp);
    };
  }

  private endStick(): void {
    if (this.stickPointer === undefined) return;
    this.stickPointer = undefined;
    this.dragX = 0;
    this.dragY = 0;
    this.emitJoystick();
  }

  private emitJoystick(): void {
    if (!this.onJoystickChange) return;
    const knob = knobOffset(this.dragX, this.dragY, TOUCH.MAX_RADIUS);
    this.onJoystickChange({
      active: this.stickPointer !== undefined,
      originX: this.originX,
      originY: this.originY,
      knobX: knob.x,
      knobY: knob.y,
    });
  }

  // --- queries ---------------------------------------------------------------

  isDown(code: string): boolean {
    return this.down.has(code);
  }

  /** True only on the frame a key first went down. */
  wasPressed(code: string): boolean {
    return this.pressedThisFrame.has(code);
  }

  /** True while the stick is being dragged — used to tailor on-screen hints. */
  get isUsingPointer(): boolean {
    return this.stickPointer !== undefined;
  }

  /** Edge-triggered restart from an on-screen tap. */
  wasRestartPressed(): boolean {
    return this.virtualRestart;
  }

  /** Called by the on-screen restart control; consumed at the next frame end. */
  pressVirtualRestart(): void {
    this.virtualRestart = true;
  }

  /** Call once at the end of each frame to clear edge-triggered state. */
  endFrame(): void {
    this.pressedThisFrame.clear();
    this.virtualRestart = false;
  }

  /**
   * Movement as a vector of length 0..1 — direction, scaled by how hard you're
   * asking. The stick wins while it's held, so a drag overrides stuck keys.
   */
  getDirection(out: Vec2): Vec2 {
    if (this.stickPointer !== undefined) {
      const r = readJoystick(this.dragX, this.dragY, TOUCH.MAX_RADIUS, TOUCH.DEAD_ZONE);
      out.x = r.x * r.magnitude;
      out.y = r.y * r.magnitude;
      return out;
    }

    const left = this.isDown('ArrowLeft') || this.isDown('KeyA');
    const right = this.isDown('ArrowRight') || this.isDown('KeyD');
    const up = this.isDown('ArrowUp') || this.isDown('KeyW');
    const down = this.isDown('ArrowDown') || this.isDown('KeyS');

    // Screen "up" is -z in world space, which is -y in gameplay coords.
    out.x = (right ? 1 : 0) - (left ? 1 : 0);
    out.y = (down ? 1 : 0) - (up ? 1 : 0);

    const len = Math.hypot(out.x, out.y);
    if (len > 0) {
      out.x /= len; // no diagonal speed boost
      out.y /= len;
    }
    return out;
  }

  dispose(): void {
    this.detach();
    this.onJoystickChange = undefined;
  }
}

/**
 * Pointer capture keeps a drag reporting to us even once it slides off the
 * element — but it throws if the pointer isn't currently active, which happens
 * with synthetic events and with races against the pointer already being
 * released. Capture is an optimisation, not a requirement: the stick still works
 * without it, so never let a failure here abort the handler mid-way.
 */
function capturePointer(target: HTMLElement, id: number): void {
  try {
    target.setPointerCapture?.(id);
  } catch {
    /* not capturable — carry on uncaptured */
  }
}

function releasePointer(target: HTMLElement, id: number): void {
  try {
    target.releasePointerCapture?.(id);
  } catch {
    /* already released */
  }
}

const MOVEMENT_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
]);
